import { Box, Tooltip } from '@mantine/core';
import {
  USER_STATUS_LEAVE,
  USER_STATUS_OFFLINE,
  USER_STATUS_ONLINE,
} from '@shared/config/runtime';
import { userStatusLabel } from '@shared/utils/ui';

function presenceColor(status: number): string {
  if (status === USER_STATUS_ONLINE) {
    return 'var(--mantine-color-green-filled)';
  }
  if (status === USER_STATUS_LEAVE) {
    return 'var(--mantine-color-yellow-filled)';
  }
  if (status === USER_STATUS_OFFLINE) {
    return 'var(--mantine-color-gray-filled)';
  }
  return 'var(--mantine-color-dimmed)';
}

/**
 * Theme-aware presentation for protobuf UserStatType.
 *
 * Mantine's semantic `*-filled` and `dimmed` variables resolve against the
 * active color scheme, so the same status remains legible in light and dark
 * mode without hard-coded RGB values or a second theme branch.
 */
export function UserPresenceIndicator({
  status,
  size = 9,
  borderColor = 'var(--mantine-color-body)',
  borderWidth,
}: {
  status: number;
  /** Final outside diameter, including the surface-separation border. */
  size?: number;
  borderColor?: string;
  /** Set to 0 when the indicator is an inline legend instead of an avatar badge. */
  borderWidth?: number;
}) {
  const label = userStatusLabel(status);
  // Small badges need a slightly thinner halo so the status fill remains
  // visually dominant. The value is dimensional only; all colors still come
  // from Mantine's light/dark-aware theme tokens.
  const resolvedBorderWidth = borderWidth ?? (size <= 9 ? 1.5 : 2);
  return (
    <Tooltip label={label} withArrow openDelay={350}>
      <Box
        component="span"
        role="img"
        aria-label={label}
        data-presence-status={status}
        w={size}
        h={size}
        style={{
          display: 'inline-block',
          flex: '0 0 auto',
          borderRadius: 'var(--mantine-radius-xl)',
          border: `${resolvedBorderWidth}px solid ${borderColor}`,
          backgroundColor: presenceColor(status),
          // `size` must describe the rendered outside diameter. With
          // content-box the old 7px contact badge actually occupied 11px,
          // making it disproportionately large beside a 32px avatar.
          boxSizing: 'border-box',
        }}
      />
    </Tooltip>
  );
}
